type PathLabel = {
  readonly labelKey: string
  readonly labelFallback: string
  readonly detail: string
}

// Deliberately index access rather than `Array.prototype.at(-1)`: `.at` is
// ES2022 (Chrome 92 / Safari 15.4) and esbuild's `build.target` lowers syntax
// but never polyfills builtin METHODS, so on an older engine `.at` is simply
// undefined. `PATTERNS[0].test` below runs on EVERY authenticated navigation
// (see pathToLabel), and this module is reached from a passive effect in Root,
// so a missing `.at` threw past the router and unmounted the whole app shell.
// Observed in production on an Android System WebView pinned to Chrome 90.
function lastSegment(segments: ReadonlyArray<string>): string | undefined {
  return segments[segments.length - 1]
}

function segmentAfter(segments: ReadonlyArray<string>, key: string): string | undefined {
  const idx = segments.indexOf(key)
  if (idx === -1 || idx >= segments.length - 1) return undefined
  return segments[idx + 1]
}

const PATTERNS: ReadonlyArray<{
  readonly test: (segments: ReadonlyArray<string>) => boolean
  readonly label: (segments: ReadonlyArray<string>) => PathLabel
}> = [
  {
    test: segments => lastSegment(segments) === 'dashboard',
    label: () => ({ labelKey: 'navigation.project.summary', labelFallback: 'Summary', detail: '' }),
  },
  {
    test: segments => lastSegment(segments) === 'notifications',
    label: () => ({
      labelKey: 'navigation.project.mail',
      labelFallback: 'Notifications',
      detail: '',
    }),
  },
  {
    test: segments => lastSegment(segments) === 'archive',
    label: () => ({
      labelKey: 'navigation.settings.global.issuesList',
      labelFallback: 'Issues List',
      detail: '',
    }),
  },
  {
    test: segments => lastSegment(segments) === 'sync-queue',
    label: () => ({ labelKey: 'syncQueue.syncQueue', labelFallback: 'Sync Queue', detail: '' }),
  },
  {
    test: segments => {
      const idx = segments.indexOf('reports')
      return idx !== -1 && idx === segments.length - 1
    },
    label: () => ({
      labelKey: 'navigation.settings.global.reports',
      labelFallback: 'Reports',
      detail: '',
    }),
  },
  {
    test: segments => {
      const idx = segments.indexOf('reports')
      return idx !== -1 && idx < segments.length - 1
    },
    label: segments => {
      const id = segmentAfter(segments, 'reports') ?? ''
      return {
        labelKey: 'navigation.settings.global.reports',
        labelFallback: 'Report',
        detail: id ? `#${id}` : '',
      }
    },
  },
  {
    test: segments => {
      const idx = segments.indexOf('issues')
      return idx !== -1 && idx < segments.length - 1
    },
    label: segments => {
      const id = segmentAfter(segments, 'issues') ?? ''
      return {
        labelKey: 'project.issues.issue',
        labelFallback: 'Issue',
        detail: id ? `#${id}` : '',
      }
    },
  },
  {
    test: segments => {
      const idx = segments.indexOf('issues')
      return idx !== -1 && idx === segments.length - 1
    },
    label: () => ({
      labelKey: 'navigation.settings.global.issues',
      labelFallback: 'Issues',
      detail: '',
    }),
  },
  {
    test: segments => segments.includes('settings'),
    label: () => ({
      labelKey: 'navigation.project.settings',
      labelFallback: 'Settings',
      detail: '',
    }),
  },
]

export function pathToLabel(pathname: string): PathLabel {
  const segments = pathname.split('/').filter(Boolean)

  for (const pattern of PATTERNS) {
    if (pattern.test(segments)) {
      return pattern.label(segments)
    }
  }

  const last = lastSegment(segments) ?? ''
  const fallback = last.charAt(0).toUpperCase() + last.slice(1)
  return { labelKey: '', labelFallback: fallback, detail: '' }
}
